// @flow
import {
  useState,
  useEffect,
  useMemo,
  useContext,
  createContext,
  type Node,
  useRef
} from 'react';
import axios from 'axios';
import type { TCApiShopType } from './types';

type ShopsBySlugType = { [string]: TCApiShopType };

const Context = createContext<{ [string]: TCApiShopType }>({});

let currentRequestSequence = 0;
let currentSlugsHash = '';

function apiShopsLoader(
  sequenceId: number,
  slugs: string[],
  apiShopsBySlug: ShopsBySlugType,
  setApiShopsBySlug: (ShopsBySlugType) => void
) {
  if (sequenceId !== currentRequestSequence) return;
  const slugsSlice = slugs.filter((slug) => !apiShopsBySlug[slug]).slice(0, 10);
  const nextSlugsSlice = slugs
    .filter((slug) => !apiShopsBySlug[slug])
    .slice(10);

  function cleanUp(apiShops = apiShopsBySlug) {
    const finalApiShops = { ...apiShops };
    let hasUpdatedApiShop = apiShops !== apiShopsBySlug;
    slugs.forEach((slug) => {
      if (!finalApiShops[slug]) {
        hasUpdatedApiShop = true;
        finalApiShops[slug] = {
          isMissing: true
        };
      }
    });
    if (hasUpdatedApiShop) {
      setApiShopsBySlug(finalApiShops);
    }
  }

  if (!slugsSlice.length) {
    cleanUp();
    return;
  }
  axios
    .post(`${CONFIG.dinerApi}/v2/shop_index`, {
      slugs: slugsSlice
    })
    .then(({ data: { shops: shopsResult } }) => {
      const updatedApiShops = shopsResult.reduce(
        (result, shop) => {
          result[shop.slug] = shop;
          return result;
        },
        { ...apiShopsBySlug }
      );
      slugsSlice.forEach((slug) => {
        if (!updatedApiShops[slug]) {
          updatedApiShops[slug] = {
            isMissing: true
          };
        }
      });
      // by doing this it will "appear" to load the page incrementally
      setApiShopsBySlug(updatedApiShops);
      if (nextSlugsSlice.length) {
        apiShopsLoader(
          sequenceId,
          nextSlugsSlice,
          updatedApiShops,
          setApiShopsBySlug
        );
      }
    })
    .catch(cleanUp);
}

export const ApiShopsProvider = ({
  slugs,
  apiShopsRef,
  children
}: {
  apiShopsRef: { current: ShopsBySlugType },
  children: Node,
  slugs: string[]
}) => {
  const [apiShopsBySlug, setApiShopsBySlug] = useState(
    apiShopsRef.current || {}
  );
  apiShopsRef.current = apiShopsBySlug;
  // if in storyblok we need to react to adding/removing/updating shops
  const reloadShopsDebounce = useRef();
  useEffect(() => {
    const newSlugsHash = JSON.stringify(slugs);
    if (newSlugsHash === currentSlugsHash) return;
    currentSlugsHash = newSlugsHash;
    if (currentRequestSequence === 0) {
      const sequenceId = currentRequestSequence + 1;
      currentRequestSequence = sequenceId;
      apiShopsLoader(sequenceId, slugs, apiShopsBySlug, (newApiShops) =>
        setApiShopsBySlug(newApiShops)
      );
    } else if (window.storyblok) {
      // This should only be used when inside the storyblok editor
      clearTimeout(reloadShopsDebounce.current);
      reloadShopsDebounce.current = setTimeout(() => {
        const sequenceId = currentRequestSequence + 1;
        currentRequestSequence = sequenceId;
        apiShopsLoader(sequenceId, slugs, apiShopsBySlug, (newApiShops) =>
          setApiShopsBySlug(newApiShops)
        );
      }, 500);
    }
  }, [slugs, apiShopsBySlug, setApiShopsBySlug]);

  return <Context.Provider value={apiShopsBySlug}>{children}</Context.Provider>;
};

export function useApiShop(slug: string) {
  const shopValue = useContext(Context)[slug];
  return useMemo(() => shopValue, [shopValue]);
}
